import makeApiRequest from '../../api/makeApiCall';
import { Config } from '../../latest/src/constants/AppServerConfig';
import { blobToBase64 } from '../../legacy/utils/helpers';
import { Logger } from 'src/latest/src/services';
import { DocumentType } from 'src/latest/src/constants/DocumentTypeEnum';
import {
  getAccessToken,
  refreshAccessToken,
} from 'src/latest/src/auth/authStore';
import { UploadDocumentWithPreSignedUrlResponse } from 'src/latest/src/services/DocumentUploadService';
import store from '../store';

const USER_SERVICE_URL = Config.userService;
const USERS_SERVICE = Config.userService;
const LOGGING_SERVICE = Config.loggingService;
const THERAPY_SESSIONS_SERVICE = Config.therapysessionservieces;
const CONFIGURATION_SERVICE = Config.configurationService;
const V2_CONFIGURATION_SERVICE = Config.v2ConfigurationService;
const VERIFICATION_SERVICE = Config.verificationService;

export const getAppConfig = async () => {
  return await makeApiRequest(
    `${CONFIGURATION_SERVICE}configurations/public`,
    'GET',
  );
};

export const getSupportConfig = async () => {
  return await makeApiRequest(
    `${CONFIGURATION_SERVICE}configurations?module=technical-support,practice-support,questionnaires-service`,
    'GET',
  );
};

export const getInsurancesConfig = async () => {
  return await makeApiRequest(
    `${CONFIGURATION_SERVICE}configurations/config-keys/INSURANCES`,
    'GET',
  );
};

export const patchTenantName = async (newTenantName: any) => {
  const requestBody = {
    value: newTenantName,
  };

  return await makeApiRequest(
    `${V2_CONFIGURATION_SERVICE}configurations/config-keys/TENANT_NAME`,
    'PATCH',
    requestBody,
  );
};

export const patchScheduling = async (updatedSchedulingData: {
  title?: string;
  email?: string;
  phone?: string;
}) => {
  const requestBody = {
    config_name: 'SCHEDULING',
    config_value: JSON.stringify(updatedSchedulingData),
  };

  return await makeApiRequest(
    `${CONFIGURATION_SERVICE}configurations/config-keys/SCHEDULING`,
    'PATCH',
    requestBody,
  );
};

export const patchSupport = async (updatedSupportData: {
  title?: string;
  email?: string;
  phone?: string;
}) => {
  const requestBody = {
    config_name: 'PROVIDER_REMATCHED',
    config_value: JSON.stringify(updatedSupportData),
  };

  return await makeApiRequest(
    `${CONFIGURATION_SERVICE}configurations/config-keys/PROVIDER_REMATCHED`,
    'PATCH',
    requestBody,
  );
};

export const patchbilling = async (updatedBillingData: {
  title?: string;
  email?: string;
  phone?: string;
}) => {
  const requestBody = {
    config_name: 'BILLING',
    config_value: JSON.stringify(updatedBillingData),
  };

  return await makeApiRequest(
    `${CONFIGURATION_SERVICE}configurations/config-keys/BILLING`,
    'PATCH',
    requestBody,
  );
};

export const patchFeedback = async (updatedFeedbackData: {
  title?: string;
  email?: string;
  phone?: string;
}) => {
  const requestBody = {
    config_name: 'FEEDBACK',
    config_value: JSON.stringify(updatedFeedbackData),
  };

  return await makeApiRequest(
    `${CONFIGURATION_SERVICE}configurations/config-keys/FEEDBACK`,
    'PATCH',
    requestBody,
  );
};

export const uploadPicture = async (data: any) => {
  return await makeApiRequest(
    `${USER_SERVICE_URL}user/profile-pic/upload`,
    'POST',
    data,
  );
};

export const getPicture = async (fileKey: string) => {
  const imageResponse = await fetch(
    `${
      Config.apiUrl + '/' + USER_SERVICE_URL
    }user/profile-pic/download/${fileKey}`,
    {
      method: 'GET',
    },
  );
  const imageSrc = await imageResponse.blob();
  const img = await blobToBase64(imageSrc);

  return img;
};

export const getPictureURL = async (fileKey: string) => {
  return `${
    Config.apiUrl + '/' + USER_SERVICE_URL
  }user/profile-pic/download/${fileKey}`;
};

export const getUserPicture = async (userId: string) => {
  const authToken = `Bearer ${getAccessToken()}`;

  const imageResponse = await fetch(
    `${Config.apiUrl + '/' + USERS_SERVICE}user/${userId}/profile-picture`,
    {
      method: 'GET',
      headers: { Authorization: authToken },
    },
  );

  const imageSrc = await imageResponse.blob();

  if (imageSrc.size === 0 && imageSrc.type === '') {
    return '';
  } else {
    const img = await blobToBase64(imageSrc);

    return img;
  }
};

export const getLastLoginDetail = async (userID: string) => {
  return await makeApiRequest(
    `${LOGGING_SERVICE}activity-log/findRecords?offset=1&limit=1`,
    'POST',
    { action: 'Logged in', actedByUserId: userID },
  );
};

export const getTherapySessionStatus = async (sessionId: string) => {
  return await makeApiRequest(
    `${THERAPY_SESSIONS_SERVICE}/therapy-sessions/${sessionId}`,
    'GET',
  );
};

// Document Service :-
// = ===========================
// UPLOAD
// 1. Get PreSigned URL -> getPreSignedUrl()
// 2. Upload Image to Presined url -> uploadImagePresignedUrl()
// 3. Confirm Uploading to Presined url

export const getPreSignedUrl: any = async (
  fileType: string,
  file: any,
  userId: string,
  intakeUserId: string,
  isFromProfile = false,
) => {
  const accessToken = getAccessToken();

  const headers = new Headers({
    'Content-Type': 'application/json',
    Authorization: 'Bearer ' + accessToken,
  });
  const options = {
    method: 'POST',
    headers,
    body: JSON.stringify({
      file_type: fileType,
      mime_type: file.type,
      name: file.name,
      size_byte: file.size,
    }),
    retryOn: [401],
    retries: 3,
    retryDelay: 1000,
  };
  let apiResponse: any = null;

  const queryParams = new URLSearchParams();

  const location = window.location.pathname === '/therapy/initquestions';

  if (intakeUserId && location) {
    queryParams.append('user_id', intakeUserId);
  }

  const apiUrl =
    intakeUserId && location
      ? `${Config.apiUrl + '/' + USERS_SERVICE}v1/document?${queryParams}`
      : `${Config.apiUrl + '/' + USERS_SERVICE}v1/document`;

  return await fetch(apiUrl, options).then(async response => {
    if (response.status === 200) {
      const responseUrl = await response.json();

      if (responseUrl.signed_url) {
        const uploadFinalStatus: any = await uploadDocumentWithPreSignedUrl(
          responseUrl.id,
          decodeURIComponent(responseUrl.signed_url),
          file,
        );

        await updateUploadStatus(
          uploadFinalStatus?.documentId,
          uploadFinalStatus?.failureReason,
          uploadFinalStatus?.isUploaded,
        );

        if (uploadFinalStatus.isUploaded) {
          let apiResponse;

          if (fileType === DocumentType.PROFILE_PIC) {
            let patchAndGetUserDetails;

            if (isFromProfile === true) {
              if (!userId) {
                apiResponse = '';

                return apiResponse;
              }

              patchAndGetUserDetails = await patchUserProfilePic(
                responseUrl.id,
                userId,
              );
            } else {
              if (!userId && !intakeUserId) {
                apiResponse = '';

                return apiResponse;
              }

              patchAndGetUserDetails = await patchUserProfilePic(
                responseUrl.id,
                userId,
                intakeUserId,
              );
            }

            apiResponse = patchAndGetUserDetails.profile_pic;

            return apiResponse;
          } else if (fileType?.includes(DocumentType.THERAPIST_CONSENT)) {
            apiResponse = responseUrl;

            return apiResponse;
          } else if (fileType === DocumentType.OFFICE_PIC) {
            const state = store.getState();
            const isDirectoryPlatform =
              state?.configuration?.isDirectoryPlatform === 'true';

            if (isDirectoryPlatform) {
              return responseUrl.id;
            }

            return responseUrl.signed_url;
          } else {
            apiResponse = '';

            return apiResponse;
          }
        } else {
          apiResponse = '';

          return apiResponse;
        }
      }

      return apiResponse;
    } else if (response.status === 401) {
      // retry uploading once with a fresh token
      try {
        await refreshAccessToken();

        return await getPreSignedUrl(fileType, file, userId);
      } catch (err) {
        Logger.error(err);

        return '';
      }
    }
  });
};

export const uploadDocumentWithPreSignedUrl = async (
  documentId: string,
  preSignedUrl: string,
  file: any,
) => {
  const body = new FormData();

  body.append('file', file);

  const convertToBase64 = await blobToBase64(file);

  const binaryBlob = await fetch(convertToBase64)
    .then(response => response.blob())
    .then(blob => {
      return blob;
    });

  const uploadData: UploadDocumentWithPreSignedUrlResponse = await fetch(
    preSignedUrl,
    {
      method: 'PUT',
      body: binaryBlob,
    },
  )
    .then(response => {
      let data: UploadDocumentWithPreSignedUrlResponse = {
        documentId: '',
        failureReason: null,
        isUploaded: false,
      };

      if (response.status === 200) {
        data = {
          documentId,
          failureReason: null,
          isUploaded: true,
        };
      } else if (!response.ok) {
        updateUploadStatus(documentId, response.statusText.toString(), false);
        data = {
          documentId,
          failureReason: response.statusText,
          isUploaded: false,
        };
      }

      return data;
    })
    .catch((err: any) => {
      Logger.error('failed response: ', err, err.status);

      return {
        documentId,
        failureReason: err.toString(),
        isUploaded: false,
      };
    });

  return uploadData;
};

export const updateUploadStatus = async (
  documentId: string,
  failureReason: string,
  isUploaded: boolean,
) => {
  return await makeApiRequest(
    `${USERS_SERVICE}v1/document/${documentId}/_upload-status`,
    'PUT',
    { failure_reason: failureReason, uploaded: isUploaded },
  );
};

export const patchUserProfilePic = async (
  documentId: string,
  userId: string,
  intakeUserId?: string,
) => {
  const location = window.location.pathname === '/therapy/initquestions';

  let SelectedUserId = '';

  if (intakeUserId && location) {
    SelectedUserId = intakeUserId;
  } else {
    SelectedUserId = userId;
  }

  return await makeApiRequest(
    `${USERS_SERVICE}v1/users/${SelectedUserId}`,
    'PATCH',
    {
      profile_pic_id: documentId,
    },
  );
};

export const resetUser2FA = async (userId: string) => {
  return await makeApiRequest(
    `${USERS_SERVICE}v1/users/${userId}/credentials/_reset-2fa`,
    'PUT',
  );
};

export const esignPDF = async (
  documentId: string,
  base64SignatureString: string,
) => {
  return await makeApiRequest(
    `${USERS_SERVICE}v1/digital-document/${documentId}/signature`,
    'POST',
    { base64: base64SignatureString },
  );
};

export const esignCustomConsent = async (
  documentId: string,
  base64String: string,
) => {
  return await makeApiRequest(
    `${USERS_SERVICE}v1/digital-document/${documentId}/generate`,
    'POST',
    { base64: base64String },
  );
};

export const deleteFile = async (fileId: string) => {
  return await makeApiRequest(
    `${USERS_SERVICE}v1/document/${fileId}`,
    'DELETE',
  );
};

export const confirmAppointment = async (
  verificationidcode: string,
  isconfirm: boolean,
) => {
  return await makeApiRequest(
    `${USERS_SERVICE}v1/todos/verification/${verificationidcode}/_follow-up?approved=${isconfirm}`,
    'POST',
  );
};

export const redeemMagicLinkToken = async (verificationToken: string) => {
  return await makeApiRequest(
    `${USERS_SERVICE}v1/users/lead-capture/redeem?token=${verificationToken}`,
    'POST',
  );
};

export const getUserEmailThroughOpaqueToken = async (OpaqueToken: string) => {
  return await makeApiRequest(
    `${USERS_SERVICE}v1/users/lead-capture/resolve-ref?ref=${OpaqueToken}`,
    'GET',
  );
};

export const sendLeadCaptureOtp = async (email: string) => {
  return await makeApiRequest(
    `${USERS_SERVICE}v1/users/lead-capture/otp/send`,
    'POST',
    { email },
  );
};

export const verifyLeadCaptureOtp = async (email: string, code: string) => {
  return await makeApiRequest(
    `${USERS_SERVICE}v1/users/lead-capture/otp/verify`,
    'POST',
    { email, code },
  );
};

export const confirmAuthorization = async (
  CONSENT_VERIFICATION_TOKEN: string,
) => {
  return await makeApiRequest(
    `${USERS_SERVICE}v1/users/_payment-consents/${CONSENT_VERIFICATION_TOKEN}/authorize`,
    'POST',
  );
};

export const getInsuranceVerificationDetails = async (userId: string) => {
  return await makeApiRequest(
    `${VERIFICATION_SERVICE}insurance/_eligibility?q_user_id=${userId}`,
    'GET',
  );
};

export const getStripePaymentMethods = async (clientId: string) => {
  return await makeApiRequest(
    `${VERIFICATION_SERVICE}payment-methods/${clientId}`,
    'GET',
  );
};

export const putStripePaymentMethods = async (
  setUpIntentId: string,
  paymentMethodId: string,
  clientId: string,
) => {
  return await makeApiRequest(
    `${VERIFICATION_SERVICE}payment-methods/${clientId}`,
    'PUT',
    {
      setup_intent_id: setUpIntentId,
      new_payment_method_id: paymentMethodId,
    },
  );
};

export const stripeSetupIntent = async (clientId: string) => {
  return await makeApiRequest(
    `${VERIFICATION_SERVICE}payment-methods/_setup-intent/${clientId}`,
    'POST',
  );
};

export const stripePaymentComplete = async (
  clientPaymentProfileId: string,
  setUpIntentId: string,
  clientID: string,
) => {
  return await makeApiRequest(
    `${VERIFICATION_SERVICE}payment-methods/${clientPaymentProfileId}/complete`,
    'PUT',
    {
      setup_intent_id: setUpIntentId,
      client_id: clientID,
    },
  );
};

export const stripeSetCardAsDefault = async (
  clientId: string,
  paymentMethodId: string,
) => {
  return await makeApiRequest(
    `${VERIFICATION_SERVICE}payment-methods/${clientId}/${paymentMethodId}/set-default`,
    'PUT',
  );
};

export const stripeCardDelete = async (
  clientId: string,
  paymentMethodId: string,
) => {
  return await makeApiRequest(
    `${VERIFICATION_SERVICE}payment-methods/${clientId}/${paymentMethodId}`,
    'DELETE',
  );
};

export const editStripeCardNickName = async (
  clientId: string,
  paymentMethodId: string,
  nickname: string,
) => {
  return await makeApiRequest(
    `${VERIFICATION_SERVICE}payment-methods/${clientId}/${paymentMethodId}/custom-data`,
    'PATCH',
    {
      nick_name: nickname,
    },
  );
};
